import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { slugify, type Category, type Draft } from "./types";

type Props = {
  draft: Draft | null;
  onChange: (next: Draft | null) => void;
  saving: boolean;
  onSave: (draft: Draft) => void;
};

export const ChangelogEntryDialog = ({ draft, onChange, saving, onSave }: Props) => {
  return (
    <Dialog open={draft !== null} onOpenChange={(o) => !o && onChange(null)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{draft?.id ? "Edit entry" : "New changelog entry"}</DialogTitle>
          <DialogDescription>
            Markdown is not rendered — keep formatting plain. Body is optional.
          </DialogDescription>
        </DialogHeader>
        {draft && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="cl-title">Title</Label>
                <Input
                  id="cl-title" value={draft.title}
                  onChange={(e) => onChange({
                    ...draft, title: e.target.value,
                    slug: draft.id ? draft.slug : slugify(e.target.value),
                  })}
                  placeholder="Image Studio gradient presets"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cl-category">Category</Label>
                <Select
                  value={draft.category}
                  onValueChange={(v) => onChange({ ...draft, category: v as Category })}
                >
                  <SelectTrigger id="cl-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feature">Feature</SelectItem>
                    <SelectItem value="improvement">Improvement</SelectItem>
                    <SelectItem value="fix">Fix</SelectItem>
                    <SelectItem value="announcement">Announcement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cl-slug">Slug</Label>
              <Input
                id="cl-slug" value={draft.slug}
                onChange={(e) => onChange({ ...draft, slug: e.target.value })}
                placeholder="image-studio-gradient-presets"
              />
              <p className="text-xs text-muted-foreground">
                Lowercase letters, numbers, dashes. Used as the anchor on /changelog.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cl-summary">Summary</Label>
              <Textarea
                id="cl-summary" rows={2} value={draft.summary}
                onChange={(e) => onChange({ ...draft, summary: e.target.value })}
                placeholder="One sentence shown in the card."
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cl-body">Body (optional)</Label>
              <Textarea
                id="cl-body" rows={6} value={draft.body}
                onChange={(e) => onChange({ ...draft, body: e.target.value })}
                placeholder="Optional details, kept as plain text."
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox" checked={draft.published}
                onChange={(e) => onChange({ ...draft, published: e.target.checked })}
              />
              Publish immediately
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onChange(null)}>Cancel</Button>
          <Button
            onClick={() => draft && onSave(draft)}
            disabled={saving}
          >
            {saving ? "Saving…" : draft?.id ? "Save changes" : "Create entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
